import { Course } from './types';
import { IMPROVED_BASIC_COURSE } from './basicCourseImproved';
import { INTERMEDIATE_COURSE } from './intermediateCourse';
import { ADVANCED_COURSE } from './advancedCourse';
import { TYPESCRIPT_REACT_COURSE } from './typescriptReactCourse';
import { REACT_TESTING_COURSE } from './reactTestingCourse';
import { REACT_PERFORMANCE_COURSE } from './reactPerformanceCourse';

// Import enhanced courses
import { AWS_COURSES } from '@/data/aws/courses';
import { JAVA_BASIC_COURSE, JAVA_INTERMEDIATE_COURSE, JAVA_ADVANCED_COURSE } from '@/data/java';
import { ETL_COURSES } from '@/data/etl';
import { SPECIALIZED_AI_COURSES } from '@/data/specialized-ai';
import { migrateAllReactModules } from './safeQuizMigration';
import { AUTOMATION_COURSES } from '@/data/automation';
import { NO_CODE_COURSES } from '@/data/no-code';
import { RUST_COURSES } from '@/data/rust';
import { EDGE_COMPUTING_COURSES } from '@/data/edge-computing';
import { GREEN_TECH_COURSES } from '@/data/green-tech';
import { CROSS_PLATFORM_COURSES } from '@/data/cross-platform';
import { MODERN_FRONTEND_COURSES } from '@/data/modern-frontend';
import { ADVANCED_API_COURSES } from '@/data/advanced-api';
import { KUBERNETES_MASTERY_COURSES } from '@/data/kubernetes-mastery';

// Apply quiz standardization to all React courses
const STANDARDIZED_COURSES = [
  IMPROVED_BASIC_COURSE,
  INTERMEDIATE_COURSE,
  ADVANCED_COURSE,
  TYPESCRIPT_REACT_COURSE,
  REACT_TESTING_COURSE,
  REACT_PERFORMANCE_COURSE
].map(course => ({
  ...course,
  modules: migrateAllReactModules(course.modules)
}));

export const COURSES = STANDARDIZED_COURSES;

// Enhanced function to get all courses including from different course types
export const getAllCourses = () => {
  const allCourses = [
    ...STANDARDIZED_COURSES,
    // Convert AWS courses to standard Course format
    ...AWS_COURSES.map(awsCourse => ({
      id: awsCourse.id,
      title: awsCourse.title,
      description: awsCourse.description,
      level: awsCourse.level,
      totalDuration: awsCourse.totalDuration,
      image: awsCourse.image,
      prerequisites: awsCourse.prerequisites,
      learningOutcomes: awsCourse.learningOutcomes,
      modules: awsCourse.modules.map(module => ({
        id: module.id,
        title: module.title,
        description: module.description,
        content: module.content,
        duration: module.duration,
        difficulty: module.difficulty,
        order: module.order,
        estimatedTime: module.estimatedTime,
        objectives: module.learningObjectives,
        learningObjectives: module.learningObjectives,
        prerequisites: module.prerequisites,
        codeExamples: module.codeExamples,
        // Handle quiz property - AWS modules use 'quiz' not 'quickCheck'
        quiz: module.quiz,
        // Don't try to access quickCheck since it doesn't exist on AWSModule
        quickCheck: undefined
      })),
      type: 'aws' as const
    })),
    // Convert ETL courses to standard Course format
    ...ETL_COURSES.map(etlCourse => ({
      id: etlCourse.id,
      title: etlCourse.title,
      description: etlCourse.description,
      level: etlCourse.level,
      totalDuration: etlCourse.totalDuration,
      image: etlCourse.image,
      prerequisites: etlCourse.prerequisites,
      learningOutcomes: etlCourse.learningOutcomes,
      modules: etlCourse.modules.map(module => ({
        id: module.id,
        title: module.title,
        description: module.description,
        content: module.content,
        duration: module.duration,
        difficulty: module.difficulty,
        order: module.order,
        estimatedTime: module.estimatedTime,
        objectives: module.learningObjectives,
        learningObjectives: module.learningObjectives,
        prerequisites: module.prerequisites,
        codeExamples: module.codeExamples,
        quiz: module.quiz,
        quickCheck: undefined
      })),
      type: 'etl' as const
    })),
    // Add all Java courses with proper integration
    JAVA_BASIC_COURSE,
    ...(JAVA_INTERMEDIATE_COURSE ? [JAVA_INTERMEDIATE_COURSE] : []),
    ...(JAVA_ADVANCED_COURSE ? [JAVA_ADVANCED_COURSE] : []),
    // Add specialized AI courses
    ...SPECIALIZED_AI_COURSES,
    // Add Automation courses
    ...AUTOMATION_COURSES.map(course => ({
      ...course,
      type: 'automation' as const
    })),
    // Add No-Code courses
    ...NO_CODE_COURSES.map(course => ({
      ...course,
      type: 'no-code' as const
    })),
    // Add Rust courses
    ...RUST_COURSES.map(course => ({
      ...course,
      type: 'rust' as const
    })),
    // Add Edge Computing courses
    ...EDGE_COMPUTING_COURSES.map(course => ({
      ...course,
      type: 'edge-computing' as const
    })),
    // Add Green Tech courses
    ...GREEN_TECH_COURSES.map(course => ({
      ...course,
      type: 'green-tech' as const
    })),
    // Add Cross-Platform courses
    ...CROSS_PLATFORM_COURSES.map(course => ({
      ...course,
      type: 'cross-platform' as const
    })),
    // Add Modern Frontend courses
    ...MODERN_FRONTEND_COURSES.map(course => ({
      ...course,
      type: 'modern-frontend' as const
    })),
    // Add Advanced API courses
    ...ADVANCED_API_COURSES.map(course => ({
      ...course,
      type: 'advanced-api' as const
    })),
    // Add Kubernetes Mastery courses
    ...KUBERNETES_MASTERY_COURSES.map(course => ({
      ...course,
      type: 'kubernetes-mastery' as const
    }))
  ];

  return allCourses;
};

// Function to get courses by type with enhanced filtering
export const getCoursesByType = (courseType: string) => {
  const allCourses = getAllCourses();
  return allCourses.filter(course => course.type === courseType || 
    (courseType === 'react' && !course.type));
};

// Function to validate course integration
export const validateCourseIntegration = () => {
  const allCourses = getAllCourses();
  const validation = {
    totalCourses: allCourses.length,
    courseTypes: [...new Set(allCourses.map(c => c.type || 'react'))],
    coursesWithQuizzes: allCourses.filter(c => 
      c.modules.some(m => m.quiz || m.quickCheck)
    ).length,
    coursesWithoutQuizzes: allCourses.filter(c => 
      !c.modules.some(m => m.quiz || m.quickCheck)
    ),
    integrationIssues: []
  };
  
  // Check for integration issues
  validation.integrationIssues = allCourses
    .filter(course => !course.modules || course.modules.length === 0)
    .map(course => `Course ${course.id} has no modules`);
  
  return validation;
};

// Enhanced validation function for quiz standardization
export const validateQuizStandardization = () => {
  const allCourses = getAllCourses();
  const validation = {
    totalCourses: allCourses.length,
    coursesWithStandardizedQuizzes: 0,
    modulesWithQuizzes: 0,
    modulesWithBackwardCompatibility: 0,
    qualityScores: [] as number[],
    integrationStatus: 'complete'
  };

  allCourses.forEach(course => {
    course.modules.forEach(module => {
      if (module.quiz && module.quiz.questions && module.quiz.questions.length >= 5) {
        validation.modulesWithQuizzes++;
        if (module.quiz.questions.length >= 5 && 
            module.quiz.questions.every(q => q.explanation)) {
          validation.coursesWithStandardizedQuizzes++;
        }
      }
      if (module.quickCheck) {
        validation.modulesWithBackwardCompatibility++;
      }
    });
  });

  return validation;
};

// Export individual courses for backward compatibility
export const BASIC_COURSE = IMPROVED_BASIC_COURSE;

// Re-export types and modules
export * from './types';
export * from './modules';
export { 
  IMPROVED_BASIC_COURSE, 
  INTERMEDIATE_COURSE, 
  ADVANCED_COURSE,
  TYPESCRIPT_REACT_COURSE,
  REACT_TESTING_COURSE,
  REACT_PERFORMANCE_COURSE
};
